import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguage, SUPPORTED_LANGS, type Lang } from '@/i18n';
import { useAuth } from '@/features/auth/useAuth';
import { setSigninLangChoice } from '@/features/auth/signinLangChoice';

/**
 * EN / SO switch (the canvas requires a visible bilingual toggle on 1h). Each
 * option is a 44px-tall tap target so a thumb can't miss it or catch the
 * neighbour.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SLICE 16 — THIS CONTROL USED TO WRITE ONLY localStorage, AND THAT WAS A BUG.
 *
 * Three individually reasonable parts combined into a silent revert:
 *   1. this component persisted the choice to localStorage and nowhere else;
 *   2. `useAuth.adoptUserLanguage` applies `users.language` from /api/me on
 *      EVERY boot and login, overwriting whatever localStorage held;
 *   3. no endpoint could write `users.language`.
 *
 * So a signed-in user who chose Soomaali was returned to English on the next
 * reload, every time, and had been since slice 04. It went unnoticed because
 * nobody reloaded while watching — and it quietly made the pending Somali
 * native review moot for anyone who did: the strings being reviewed were not
 * reaching the user who asked for them.
 *
 * Signed in, the preference is a SERVER fact and goes through `updateProfile`.
 * Signed out (the login screen) there is no user row to write, so it stays a
 * local-only choice — and that is correct, not a leftover: it is the pre-auth
 * layer `resolveInitialLang` reads, and it is adopted-over on sign-in by design.
 */
export function LangToggle() {
  const { t, i18n } = useTranslation();
  const { state, updateProfile } = useAuth();
  const [pending, setPending] = useState<Lang | null>(null);

  const current = (SUPPORTED_LANGS as readonly string[]).includes(i18n.language)
    ? (i18n.language as Lang)
    : 'en';

  async function choose(lng: Lang) {
    if (lng === current || pending) return;

    if (state.status !== 'authenticated') {
      // Pre-auth: local only — there is no row to write yet. But record that the
      // user made an EXPLICIT choice on the sign-in screen (66q): the next login()
      // consumes this marker to make the tap win over the account language and
      // write it back. One-shot, in-memory, cleared on consume/sign-out.
      setSigninLangChoice(lng);
      setLanguage(lng, { persist: true });
      return;
    }

    // Apply immediately so the switch feels instant on a slow Mogadishu link,
    // then let the server's answer be adopted by `updateProfile`. On failure we
    // put the language BACK — leaving the UI in a language the server does not
    // hold would recreate the revert-on-reload defect one release later.
    setLanguage(lng, { persist: true });
    setPending(lng);
    try {
      await updateProfile({ language: lng });
    } catch {
      setLanguage(current, { persist: true });
    } finally {
      setPending(null);
    }
  }

  return (
    <div
      role="group"
      aria-label={t('lang.en') + ' / ' + t('lang.so')}
      className="inline-flex overflow-hidden rounded-full border border-raad-line-12 p-0.5"
    >
      {SUPPORTED_LANGS.map((lng) => {
        const active = lng === current;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => void choose(lng)}
            disabled={pending !== null}
            aria-pressed={active}
            className={[
              // Compact segmented toggle (~30px tall) — standard SaaS density (Mahad,
              // 28 Oct). The active option is brand chrome (blue).
              'min-h-[30px] rounded-full px-3 text-xs font-semibold transition-colors disabled:opacity-70',
              active
                ? 'bg-raad-primary text-raad-onprimary'
                : 'text-raad-muted hover:text-raad-text',
            ].join(' ')}
          >
            {t(`lang.${lng}`)}
          </button>
        );
      })}
    </div>
  );
}
